import React, { useMemo } from "react";

import { ReactComponent as ArrowDown } from "../../assets/images/arrow-down.svg";
import { ReactComponent as ArrowUp } from "../../assets/images/arrow-up.svg";
import {
	ICON_FB,
	ICON_HOME,
	ICON_LINK,
	ICON_LINKEDIN,
	ICON_MAIL,
	ICON_PUSH,
	ICON_TWITTER,
	ICON_YOUTUBE,
} from "../../constants/icons";
import { formatTimeSinceShort } from "../../hooks/formatTimeSince";
import { formatFullNumber } from "../../hooks/formatNumber";
import { formatReadingTime } from "../../hooks/formatReadingTime";
import { formatTotalReadingTime } from "../../hooks/formatTotalReadingTime";
import Line from "../widgets/listWidget/listItem/Line";
import { formatDate } from "../admin/helper/formatters";
import { IListDataItem } from "../../constants/interfaces";
import useGetEnvironment from "../../hooks/useGetEnvironment";

type Props = {
	data: IListDataItem;
	lineSum: number;
	lineWidth: number;
	numberFields: Array<string>;
	showBrand: boolean;
	showSection: boolean;
	variant: string;
	showNotifications: boolean;
};

const formatSection = (sections: Array<string> | string) => {
	if (!sections) {
		return '';
	}

	const capitalize = (input: string) => {
		const words = input.replaceAll('-', ' ').split(' ');
		const uppercaseWords = words.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
		return `${uppercaseWords.join(' ')}`;
	};

	return Array.isArray(sections) ? sections.map(capitalize).join(', ') : capitalize(sections);
}

const ListItem = (props: Props) => {
	// if channel is in story config, show as active
	const placeIcons = (secondary_posts: Array<{ post_type: string }>) => {
		const icons = [
			"fb_post",
			"tweet",
			"newsletter",
			"homepage_link",
			"site_link",
			"linkedin",
			"pushmessage",
			"youtube",
		];
		const activeIcons = Array.isArray(secondary_posts) ? secondary_posts.map((item) => item.post_type) : [];

		return icons.map((icon, index) => {
			switch (icon) {
				case "fb_post":
					return (
						<ICON_FB
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "tweet":
					return (
						<ICON_TWITTER
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "newsletter":
					return (
						<ICON_MAIL
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "homepage_link":
					return (
						<ICON_HOME
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "site_link":
					return (
						<ICON_LINK
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "linkedin":
					return (
						<ICON_LINKEDIN
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "pushmessage":
					return (
						<ICON_PUSH
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				case "youtube":
					return (
						<ICON_YOUTUBE
							active={activeIcons.indexOf(icon) !== -1}
							key={index}
						/>
					);
				default:
					return <React.Fragment key={index}></React.Fragment>;
			}
		});
	};

	const {
		data,
		lineSum,
		lineWidth,
		numberFields,
		showBrand,
		showSection,
		variant,
	} = props;
	const {
		timestamp,
		title,
		author,
		brandName,
		section,
		trend,
		notifications,
		secondary_posts,
	} = data;
	const add = trend >= 0;
	const getEnvironment = useGetEnvironment();

	const createUrl = useMemo(()=>{
		let url = "";
		const environment = getEnvironment();
		if (environment === "staging" || environment === "staging2") {
			url = `https://${data.brandId}-${environment}.smartocto.com/#/stories/${data.relationId}/`
		} else {
			url = `https://${data.brandId}.smartocto.com/#/stories/${data.relationId}/`;
		}
		return url
	},[data.brandId, data.relationId, data.type, getEnvironment])

	return (
		<div className="list-item">
			<div className="list-item__row list-item__row--social-icons">
				<p className="list-item__timeframe">
					{formatTimeSinceShort(Date.parse(timestamp))}
				</p>
				{placeIcons(secondary_posts)}
				{variant === "full" && author ? (
					<p className="list-item__author">{author}</p>
				) : null}
			</div>
			<div className="list-item__row">
				<div className="list-item__header">
					{add ? (
						<ArrowUp className="list-item__arrow" />
					) : (
						<ArrowDown className="list-item__arrow" />
					)}
					<h3 className="list-item__title ">
						{createUrl ? (
							<a href={createUrl} target="_blank" rel="noreferrer">
								{title}
							</a>
						) : (
							title
						)}
					</h3>
				</div>
				<div
					className={`list-item-stats dynamic-flex-${
						numberFields.length +
						Number(variant === "full" && showSection) +
						(isNaN(Number(brandName)) ? 0 : 1)
					}`}
				>
					{numberFields.map((field, idx) => {
						if (data[field]) {
							if (field === "pubdate") {
								return (
									<span
										className="list-item-stat date"
										key={idx}
									>
										{formatDate(data[field])}
									</span>
								);
							}
							if (field === "averageReadingTime") {
								return (
									<span
										className="list-item-stat readtime"
										key={idx}
									>
										{formatReadingTime(data[field])}
									</span>
								);
							}
							if (field === "conversion") {
								return (
									<span
										className="list-item-stat readtime"
										key={idx}
									>
										{formatFullNumber(data[field])}
									</span>
								);
							}
							if (field === "readingTimeTotal") {
								return (
									<span
										className="list-item-stat readtime"
										key={idx}
									>
										{formatTotalReadingTime(data[field])}
									</span>
								);
							}
							return (
								<span className="list-item-stat" key={idx}>
									{formatFullNumber(data[field])}
								</span>
							);
						} else {
							return (
								<span className="list-item-stat" key={idx}>
									n/a
								</span>
							);
						}
					})}
					{variant === "full" && showSection && (
						<div className="list-item-stat list-item__extra">
							{showSection && section && (
								<p className="list-item__section">
									{formatSection(section)}
								</p>
							)}
							{props.showNotifications && (
								<p className="list-item__actions">
									<span>
										<span>{notifications}</span>
									</span>
								</p>
							)}
						</div>
					)}
					{showBrand && brandName && (
						<div className="list-item-stat list-item__extra">
							<div className="list-item__brand">
								<span>{brandName}</span>
							</div>
						</div>
					)}
				</div>
			</div>
			{data.isPaid && <div className="list-item__paid"></div>}
			<Line data={data} width={lineWidth} sum={lineSum} />
		</div>
	);
};

export default ListItem;
